import { useEffect } from 'react';
import { createLogger } from './debugLogger';

// Create debug logger for this module
const logger = createLogger('sessionManager', 'Session management and user activity tracking', false, 'INFO');

// Default timeout value (10 minutes in milliseconds)
export const DEFAULT_TIMEOUT = 10 * 60 * 1000;

// Session activity events to monitor
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click'
];

// Main session manager hook
export const useSessionTimeout = (onTimeout, timeoutDuration = DEFAULT_TIMEOUT) => {
  // Function to reset timer on user activity
  const resetTimer = () => {
    const now = Date.now();
    // Store the last activity time in localStorage so it can be accessed by other components
    localStorage.setItem('lastActivity', now.toString());
  };

  // Setup session timeout
  useEffect(() => {
    // Don't set up timeout if no callback or timeout value is 0
    if (!onTimeout || timeoutDuration === 0) return;
    
    // Initialize lastActivity in localStorage if not present
    if (!localStorage.getItem('lastActivity')) {
      localStorage.setItem('lastActivity', Date.now().toString());
    }
    
    // Create a new timer that checks inactivity
    // (any previous interval was already cleared by this effect's own cleanup below,
    // which runs automatically before React re-invokes this effect)
    const newTimer = setInterval(() => {
      const now = Date.now();
      
      // Check if "keep me logged in for today" is active
      const keepLoggedInUntil = localStorage.getItem('keepLoggedInUntil');
      if (keepLoggedInUntil) {
        const expirationTime = parseInt(keepLoggedInUntil, 10);
        // If keepLoggedInUntil is still valid, don't trigger timeout
        if (now < expirationTime) {
          logger.debug('Keep me logged in is active, skipping inactivity check');
          return;
        }
      }
      
      // Get the latest activity time from localStorage
      const lastActivityTime = parseInt(localStorage.getItem('lastActivity') || Date.now(), 10);
      const timeSinceLastActivity = now - lastActivityTime;
      
      if (timeSinceLastActivity >= timeoutDuration) {
        // User has been inactive for the duration threshold
        clearInterval(newTimer);
        onTimeout();
      }
    }, 5000); // Check every 5 seconds
    
    // Add event listeners for user activity
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, resetTimer);
    });
    
    // Cleanup function
    return () => {
      clearInterval(newTimer);
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
    // resetTimer is intentionally NOT a dependency: the interval callback
    // above always re-reads the latest activity timestamp from localStorage
    // itself, so this effect's closure never needs a fresh resetTimer.
    // Depending on it would tear down and rebuild the interval plus all 6
    // window listeners on every single mousemove/click/etc during an active
    // session, since resetTimer is a new function reference every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onTimeout, timeoutDuration]);

  return {
    resetTimer,
    // Function to get the remaining time in ms before timeout
    getRemainingTime: () => {
      const lastActivityTime = parseInt(localStorage.getItem('lastActivity') || Date.now(), 10);
      return Math.max(0, timeoutDuration - (Date.now() - lastActivityTime));
    }
  };
};

// Utility functions
export const getSessionTimeoutFromStorage = () => {
  const timeout = localStorage.getItem('sessionTimeout');
  return timeout ? parseInt(timeout, 10) : DEFAULT_TIMEOUT;
};

export const saveSessionTimeoutToStorage = (timeout) => {
  localStorage.setItem('sessionTimeout', timeout);
};

export default useSessionTimeout; 